'use client';

import React, { memo, useCallback, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import equal from 'fast-deep-equal';
import { CheckIcon, Loader2, SquareArrowOutUpRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import posthog from 'posthog-js';

import { ShareIcon } from '@repo/ui/components/icons/share-icon';
import { Button } from '@repo/ui/components/ui/button';
import { cn, formatDateDistanceToNowDynamic } from '@repo/ui/lib/utils';

import { ArticleCardData } from '@/hooks/api/article/model';
import { useArticleShare } from '@/hooks/use-article-share';
import { usePostMessage } from '@/providers/post-message-provider';

const brandsWithSmallLogo = ['ABC News', 'Al Jazeera (Qatar)'];

interface ArticleCardHeaderProps {
  article: ArticleCardData;
  hideAnchor?: boolean;
  isHackerNews?: boolean;
}

const PureArticleCardHeader: React.FC<ArticleCardHeaderProps> = ({
  article,
  hideAnchor = false,
  isHackerNews = false,
}) => {
  const { sendRedirectMessage } = usePostMessage();
  const router = useRouter();
  const {
    handleShare,
    isSharingUrlPending,
    isSharingUrlSuccess,
    isSharingUrlCopied,
    setIsSharingUrlCopied,
  } = useArticleShare();

  const isSmallLogo = useMemo(
    () => brandsWithSmallLogo.includes(article.brand?.brand ?? ''),
    [article.brand?.brand],
  );

  const formattedDate = useMemo(() => {
    const publishedAt = article.publishedAt;
    const createdAt = article.createdAt;

    if (isHackerNews) {
      const date = createdAt || publishedAt;
      if (!date) return null;
      return formatDateDistanceToNowDynamic(date).replace('about ', '');
    }
    if (!publishedAt) return null;

    return formatDateDistanceToNowDynamic(publishedAt).replace('about ', '');
  }, [article.createdAt, article.publishedAt, isHackerNews]);

  const redirectToArticle = useCallback(
    (e: React.MouseEvent, isNewTab?: boolean) => {
      e.stopPropagation();

      // Detect if user intends to open link in new tab
      const shouldOpenInNewTab =
        e.ctrlKey || // Ctrl+click on Windows/Linux
        e.metaKey || // Cmd+click on macOS
        e.shiftKey || // Shift+click (opens in new window/tab)
        e.button === 1; // Middle mouse button click

      sendRedirectMessage(article.url, isNewTab ?? shouldOpenInNewTab);
    },
    [article.url, sendRedirectMessage],
  );

  useEffect(() => {
    if (isSharingUrlSuccess) {
      setIsSharingUrlCopied(true);
      const timeout = setTimeout(() => {
        setIsSharingUrlCopied(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isSharingUrlSuccess, setIsSharingUrlCopied]);

  return (
    <div className="flex h-[15px] w-full items-center gap-1.5 text-[#757575] cred-md:h-[18px] cred-md:gap-[7px] cred-lg:h-[19px] cred-lg:gap-[7.6px] cred-xl:h-[25px] cred-xl:gap-2.5">
      {isHackerNews ? (
        <>
          <div className="max-w-full shrink-0 min-w-0">
            <Image
              src="/images/brand-logos/logo-hackernews.png"
              alt="Hacker News logo"
              unoptimized
              width={700}
              height={140}
              className="block size-auto object-contain max-h-3.5 cred-md:max-h-4 cred-lg:max-h-[18px] cred-xl:max-h-6"
            />
          </div>
          <span className="shrink-0 text-[10px] leading-[15px] cred-md:text-sm cred-md:leading-4 cred-lg:text-[15.2px] cred-lg:leading-[18px] cred-xl:text-xl cred-xl:leading-[23px]">
            •
          </span>
          <Button
            role="link"
            title={new URL(article.url).hostname.replace('www.', '')}
            onClick={redirectToArticle}
            variant="link"
            className="p-0 text-inherit size-auto min-w-0"
          >
            <span className="truncate text-[10px] text-black font-normal leading-[15px] cred-md:text-xs cred-md:leading-[18px] cred-lg:text-[13px] cred-lg:leading-[19px] cred-xl:text-base cred-xl:leading-4">
              {new URL(article.url).hostname.replace('www.', '')}
            </span>
          </Button>
        </>
      ) : article.brand?.logoLargeUrl ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (article.brand) {
              router.push(`/brands/${article.brand.id}`);
            }
          }}
          className="max-w-full shrink min-w-0"
        >
          <Image
            src={article.brand.logoLargeUrl}
            alt={`${article.brand.brand} logo`}
            unoptimized
            width={300}
            height={48}
            className={cn(
              'block size-auto object-contain',
              isSmallLogo
                ? 'max-h-[18px] cred-md:max-h-5 cred-lg:max-h-[21.5px] cred-xl:max-h-6'
                : 'max-h-3.5 cred-md:max-h-4 cred-lg:max-h-[17.73px] cred-xl:max-h-[23px]',
            )}
          />
        </div>
      ) : (
        <span className="shrink-0 text-xs leading-[15px] cred-md:text-sm cred-md:leading-[18px] cred-lg:text-base cred-lg:leading-[19px] cred-xl:text-sm cred-xl:leading-4">
          {article.brand?.brand || ''}
        </span>
      )}
      <span className="shrink-0 text-[10px] leading-[15px] cred-md:text-sm cred-md:leading-4 cred-lg:text-[15.2px] cred-lg:leading-[18px] cred-xl:text-xl cred-xl:leading-[23px]">
        •
      </span>
      <span className="shrink-0 text-[10px] leading-[15px] cred-md:text-xs cred-md:leading-[18px] cred-lg:text-[13px] cred-lg:leading-[19px] cred-xl:text-base cred-xl:leading-4">
        {formattedDate}
      </span>
      <div className="ml-auto shrink-0 flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          disabled={isSharingUrlPending}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleShare({
              articleId: article.id,
              shareUrl: article.sharingUrl,
            });
            posthog.capture('share_article_card', {
              article_id: article.id,
              article_title: article.title,
              article_url: article.url,
            });
          }}
          className="size-[26px] text-black [&_svg]:size-[18px] stroke-[1.7px]"
        >
          <AnimatePresence mode="wait">
            {isSharingUrlPending ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                }}
                transition={{ duration: 0.05, ease: 'easeInOut' }}
              >
                <Loader2 className="animate-spin" />
              </motion.div>
            ) : isSharingUrlCopied ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transition: { duration: 0.08, ease: 'easeInOut' },
                }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <CheckIcon />
              </motion.div>
            ) : (
              <motion.div
                key="share"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transition: { duration: 0.08, ease: 'easeInOut' },
                }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <ShareIcon />
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
        {!hideAnchor && (
          <Button
            asChild
            size="icon"
            variant="ghost"
            className="size-[26px] text-black [&_svg]:size-[18px]"
          >
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              onMouseDown={(e) => redirectToArticle(e, true)}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <SquareArrowOutUpRight />
            </a>
          </Button>
        )}
        {/* <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-[26px] text-black [&_svg]:size-[18px]"
            >
              <span className="sr-only">Follow/Unfollow source</span>
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-32 bg-white p-0 cred-md:min-w-40 cred-lg:min-w-48 cred-xl:min-w-56"
          >
            <DropdownMenuItem
              onSelect={() => {
                // TODO: follow/unfollow source
              }}
              className="h-auto cursor-pointer gap-2 rounded-b-none py-1.5 pl-2 pr-3 text-black focus:text-black cred-md:py-2.5 cred-md:pl-3 cred-md:pr-4 cred-lg:py-2.5 cred-lg:pl-3 cred-lg:pr-4 cred-xl:py-3 cred-xl:pl-4 cred-xl:pr-5 focus:bg-[#F1F5F9] active:bg-[#E5F2FF]"
            >
              <Minus className="stroke-[2.8px] transition-opacity duration-300" />
              <span className="text-sm leading-tight cred-md:text-base cred-lg:text-base cred-xl:text-lg">
                Unfollow source
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu> */}
      </div>
    </div>
  );
};
export const ArticleCardHeader = memo(
  PureArticleCardHeader,
  (prevProps, nextProps) => {
    if (prevProps.isHackerNews !== nextProps.isHackerNews) {
      return false;
    }
    if (prevProps.hideAnchor !== nextProps.hideAnchor) {
      return false;
    }
    if (!equal(prevProps.article, nextProps.article)) {
      return false;
    }
    return true;
  },
);
